// src/components/ShippingModal.js 
import React, { useState } from 'react';
import axios from 'axios';

const ShippingModal = ({ order, onClose, onSuccess }) => {
  const [carrier, setCarrier] = useState('Canada Post');
  const [service, setService] = useState('Regular Parcel');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Package details with defaults
  const [packageDetails, setPackageDetails] = useState({
    weight: 1, // Default to 1kg
    length: 20, // Default dimensions in cm
    width: 15,
    height: 10
  });

  const carriers = [
    { id: 'canadapost', name: 'Canada Post' },
    { id: 'fedex', name: 'FedEx' },
    { id: 'ups', name: 'UPS' }
  ];

  const services = {
    'Canada Post': [
      { id: 'DOM.EP', name: 'Regular Parcel' },
      { id: 'DOM.XP', name: 'Xpresspost' },
      { id: 'DOM.PC', name: 'Priority' }
    ],
    'FedEx': [
      { id: 'FEDEX_GROUND', name: 'Ground' },
      { id: 'FEDEX_EXPRESS', name: 'Express' }
    ],
    'UPS': [
      { id: 'UPS_STANDARD', name: 'Standard' },
      { id: 'UPS_EXPRESS', name: 'Express' }
    ]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPackageDetails({
      ...packageDetails,
      [name]: parseFloat(value) || 0
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/orders/${order._id}/ship`,
        {
          carrier,
          service: services[carrier].find(s => s.name === service)?.id || service,
          packageDetails: {
            serviceCode: services[carrier].find(s => s.name === service)?.id,
            ...packageDetails
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onSuccess(response.data.order);
    } catch (err) {
      console.error('Error shipping order:', err);
      setError(err.response?.data?.error || 'Error processing shipping request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Ship Order #{order._id.slice(-6)}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shipping Carrier
            </label>
            <select
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value);
                // Reset service when carrier changes
                setService(services[e.target.value][0].name);
              }}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {carriers.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Type
            </label>
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {services[carrier].map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          
          <div className="border rounded-md p-4 bg-gray-50">
            <h4 className="font-medium mb-2">Package Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  name="weight"
                  value={packageDetails.weight}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0.1"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Length (cm)
                </label>
                <input
                  type="number"
                  name="length"
                  value={packageDetails.length}
                  onChange={handleInputChange}
                  step="1"
                  min="1"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Width (cm)
                </label>
                <input
                  type="number"
                  name="width"
                  value={packageDetails.width}
                  onChange={handleInputChange}
                  step="1"
                  min="1"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Height (cm)
                </label>
                <input
                  type="number"
                  name="height"
                  value={packageDetails.height}
                  onChange={handleInputChange}
                  step="1"
                  min="1"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="bg-white text-gray-700 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Processing...' : 'Create Shipping Label'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingModal;